// ws-before-after.js — Latency Studio
// Drag/touch/keyboard handler for the WS - Before & After image comparison
// slider, plus an optional scroll-driven entry animation that sweeps the
// divider from 100% (far right) to the merchant's initial position as the
// section scrolls into view. Manual drag or keyboard input locks the
// scroll-driven mode off for the page lifetime.
// Idempotent — safe to load multiple times.

(function () {
  if (window.__wsBeforeAfterBound) return;
  window.__wsBeforeAfterBound = true;

  const reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function setPosition(section, percent) {
    const pct = clamp(percent, 0, 100);
    section.style.setProperty('--ws-ba-position', pct + '%');
    const compare = section.querySelector('.ws_before-after__compare');
    if (compare) compare.setAttribute('aria-valuenow', Math.round(pct));
  }

  function pointerToPercent(compare, clientX) {
    const rect = compare.getBoundingClientRect();
    if (rect.width === 0) return 50;
    return ((clientX - rect.left) / rect.width) * 100;
  }

  function bindScrollAnim(section) {
    if (section.dataset.wsScrollAnim !== 'true') return;
    if (reducedMotion) return;

    const startPct = parseFloat(section.dataset.wsScrollAnimStart);
    const endPct = parseFloat(section.dataset.wsScrollAnimEnd);
    const startPosition = isNaN(startPct) ? 100 : startPct;
    const endPosition = isNaN(endPct) ? 50 : endPct;
    const pauseOnHover = section.dataset.wsScrollAnimPauseOnHover !== 'false';

    setPosition(section, startPosition);

    let inView = false;
    let hoverPaused = false;
    let rafId = null;
    let onScroll = null;

    function compute() {
      rafId = null;
      if (section.__wsBaLocked || !inView || hoverPaused) return;

      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;

      // progress = 0 when the section's top edge is at the bottom of the viewport
      //            (the section is just entering)
      // progress = 1 when the section is centered in the viewport
      //            (top = vh/2 − height/2)
      const distance = vh / 2 + rect.height / 2;
      if (distance <= 0) return;
      const traveled = vh - rect.top;
      const progress = clamp(traveled / distance, 0, 1);

      const next = startPosition + (endPosition - startPosition) * progress;
      setPosition(section, next);
    }

    if (pauseOnHover) {
      section.addEventListener('mouseenter', function () {
        hoverPaused = true;
      });
      section.addEventListener('mouseleave', function () {
        hoverPaused = false;
        // Snap to current scroll position when the visitor moves away.
        if (inView && !section.__wsBaLocked) schedule();
      });
    }

    function schedule() {
      if (rafId != null) return;
      rafId = requestAnimationFrame(compute);
    }

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting;
            if (section.__wsBaLocked) {
              io.disconnect();
              return;
            }
            if (inView) {
              if (!onScroll) {
                onScroll = schedule;
                window.addEventListener('scroll', onScroll, { passive: true });
                window.addEventListener('resize', onScroll);
              }
              compute();
            } else if (onScroll) {
              window.removeEventListener('scroll', onScroll);
              window.removeEventListener('resize', onScroll);
              onScroll = null;
            }
          });
        },
        { rootMargin: '0px', threshold: 0 }
      );
      io.observe(section);

      section.__wsBaScrollCleanup = function () {
        if (onScroll) {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
          onScroll = null;
        }
        if (rafId != null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        io.disconnect();
      };
    } else {
      // No IntersectionObserver — leave the divider at the start position.
      // The drag/keyboard handlers below still work.
    }
  }

  function lock(section) {
    if (section.__wsBaLocked) return;
    section.__wsBaLocked = true;
    section.dataset.wsBaLocked = 'true';
    if (section.__wsBaScrollCleanup) section.__wsBaScrollCleanup();
  }

  function bind(section) {
    if (section.__wsBaBound) return;
    section.__wsBaBound = true;

    const compare = section.querySelector('.ws_before-after__compare');
    if (!compare) return;

    let dragging = false;

    function onPointerDown(e) {
      lock(section);
      dragging = true;
      compare.setPointerCapture && compare.setPointerCapture(e.pointerId);
      setPosition(section, pointerToPercent(compare, e.clientX));
    }

    function onPointerMove(e) {
      if (!dragging) return;
      setPosition(section, pointerToPercent(compare, e.clientX));
    }

    function onPointerUp(e) {
      dragging = false;
      try { compare.releasePointerCapture && compare.releasePointerCapture(e.pointerId); } catch (_) {}
    }

    compare.addEventListener('pointerdown', onPointerDown);
    compare.addEventListener('pointermove', onPointerMove);
    compare.addEventListener('pointerup', onPointerUp);
    compare.addEventListener('pointercancel', onPointerUp);

    // Click anywhere on the compare area to jump the handle there
    compare.addEventListener('click', function (e) {
      if (e.target.closest('.ws_before-after__handle')) return;
      lock(section);
      setPosition(section, pointerToPercent(compare, e.clientX));
    });

    // Keyboard: arrow keys move the handle in 5% steps, Home/End jump to ends
    compare.addEventListener('keydown', function (e) {
      const current = parseFloat(compare.getAttribute('aria-valuenow')) || 50;
      let next = current;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next = current - 5;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next = current + 5;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = 100;
          break;
        default:
          return;
      }
      e.preventDefault();
      lock(section);
      setPosition(section, next);
    });

    bindScrollAnim(section);
  }

  function init() {
    document.querySelectorAll('[data-ws-before-after]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
